import { getFunctions, httpsCallable } from "firebase/functions";
import { auth as authVendedor } from "../firebase";
import { erroDeVerificacao, MENSAGEM_VERIFICACAO } from "./appCheck";

/*
 * Mercado Pago do vendedor — lado do app. Aqui só há chamadas ao backend:
 * nenhum access token, refresh token ou client secret passa pelo app.
 *
 * Onde a integração aparece:
 *  - TESTE: fora do build de produção, com REACT_APP_MP_AMBIENTE=teste
 *    (npm run start:mp, nos emuladores);
 *  - PRODUÇÃO: em todo build de produção (.env.production) — site, desktop e
 *    Android têm Pix do Mercado Pago, status e alertas. CONECTAR a conta, só no
 *    site (podeConectarMercadoPago): a volta da autorização é para lá.
 */

// Endereço do site para onde o Mercado Pago devolve depois da autorização
// (functions/.env.matriz3dsite → MP_APP_URL)
export const SITE_CONEXAO_MP = "https://printcontrol.netlify.app";

const AMBIENTE_PEDIDO = process.env.REACT_APP_MP_AMBIENTE;
export const MP_AMBIENTE = process.env.NODE_ENV !== "production"
  ? (AMBIENTE_PEDIDO === "teste" ? "teste" : null)
  : (AMBIENTE_PEDIDO === "producao" && process.env.REACT_APP_FIREBASE_EMULADORES !== "true" ? "producao" : null);
export const mpHabilitado = () => MP_AMBIENTE !== null;
// Conectar a conta só onde a volta da autorização cai: no teste, o app local;
// em produção, o site. No desktop/Android (file://, capacitor) o card manda para o site.
export const podeConectarMercadoPago = () =>
  MP_AMBIENTE === "teste" || (typeof window !== "undefined" && window.location.origin === SITE_CONEXAO_MP);

/* Mercado Pago em uso: integração disponível, conta conectada e a chave
   "Usar o Mercado Pago" ligada (Configurações → Pagamentos). Em uso, a chave
   PIX manual fica guardada mas não aparece como forma de pagamento (loja e
   Pedidos) — o Pix passa a ser o do Mercado Pago. */
export const mercadoPagoEmUso = (paymentConfig, statusMP) =>
  mpHabilitado() && !!statusMP?.conectado && paymentConfig?.mercadoPagoAtivo !== false;

const CHAVE_VINCULO = "pc_mp_vinculo";
const CHAVE_RETORNO = "pc_mp_retorno";
const DEZ_MIN = 10 * 60 * 1000;

function chamar(nome, dados, app = authVendedor.app) {
  return httpsCallable(getFunctions(app, "us-central1"), nome)(dados).then(r => r.data);
}

// ── Conexão (Configurações → Pagamentos) ─────────────────────────
/* O "vínculo" não é credencial: é um valor aleatório que prova que foi ESTA
   aba que iniciou a conexão. Fica só nesta aba (sessionStorage), vale 10
   minutos e é apagado ao ser usado. */
// opcoes.trocar: provedor atual (troca confirmada na tela); opcoes.termos: versão dos termos vistos
// funcao: a loja parceira usa as próprias (mpLojaParceira*), sem plano
export async function iniciarConexao(opcoes = {}, funcao = "mpConexaoIniciar") {
  const { url, vinculo, expiraEm } = await chamar(funcao, {
    ...(opcoes.trocar ? { trocar: opcoes.trocar } : {}), ...(opcoes.termos ? { termos: opcoes.termos } : {}),
  });
  try { sessionStorage.setItem(CHAVE_VINCULO, JSON.stringify({ vinculo, expiraEm: Math.min(expiraEm, Date.now() + DEZ_MIN) })); } catch {}
  const destino = new URL(url);
  if (destino.origin !== "https://auth.mercadopago.com") throw new Error("Endereço de autorização inesperado.");
  window.location.assign(destino.toString());
}

/* Na volta do Mercado Pago o backend manda o navegador para
   /#mp_conexao=<status>&r=<recibo>. Lê e APAGA do endereço na hora (nada fica
   no histórico) e guarda na aba até a tela de Pagamentos concluir. */
export function capturarRetornoConexao() {
  const hash = window.location.hash || "";
  if (!hash.includes("mp_conexao=")) return null;
  const params = new URLSearchParams(hash.slice(1));
  const retorno = { status: params.get("mp_conexao") || "erro", recibo: params.get("r") || null };
  try { window.history.replaceState(null, "", window.location.pathname + window.location.search); } catch {}
  try { sessionStorage.setItem(CHAVE_RETORNO, JSON.stringify(retorno)); } catch {}
  return retorno;
}

export function retornoPendente() {
  try { return JSON.parse(sessionStorage.getItem(CHAVE_RETORNO) || "null"); } catch { return null; }
}

export async function concluirConexao(retorno, funcao = "mpConexaoConcluir") {
  let salvo = null;
  try {
    salvo = JSON.parse(sessionStorage.getItem(CHAVE_VINCULO) || "null");
    sessionStorage.removeItem(CHAVE_VINCULO);
    sessionStorage.removeItem(CHAVE_RETORNO);
  } catch {}
  if (retorno?.status !== "retornou" || !retorno.recibo) throw new Error(MENSAGENS_RETORNO[retorno?.status] || MENSAGENS_RETORNO.erro);
  if (!salvo?.vinculo || !(salvo.expiraEm > Date.now())) throw new Error("A conexão precisa ser concluída na mesma aba em que começou, em até 10 minutos. Comece de novo.");
  return chamar(funcao, { recibo: retorno.recibo, vinculo: salvo.vinculo });
}

export const MENSAGENS_RETORNO = {
  negado: "A autorização foi cancelada no Mercado Pago. Nada foi conectado.",
  expirado: "O tempo para autorizar acabou (10 minutos). Comece de novo.",
  usado: "Este retorno do Mercado Pago já foi usado. Comece de novo.",
  invalido: "Retorno do Mercado Pago inválido. Comece de novo.",
  erro: "Não foi possível concluir a conexão com o Mercado Pago. Tente de novo.",
};

export const desconectarMercadoPago = () => chamar("mpConexaoDesconectar", {});

// ── Pix de um pedido do vendedor ─────────────────────────────────
// simularAprovacao: só tem efeito no ambiente de TESTE (o backend ignora em produção)
export const gerarPixPedido = ({ pedidoId, requestId, email, simularAprovacao = false }) =>
  chamar("mpPixPedido", { pedidoId, requestId, email: email || undefined, simularAprovacao: MP_AMBIENTE === "teste" && simularAprovacao === true });
export const verificarPagamentoPedido = cobrancaId => chamar("mpConsultarPedido", { cobrancaId });

// ── Pix de uma venda no PDV ──────────────────────────────────────
// O servidor confere o total com os itens e, quando o Mercado Pago confirma,
// registra a venda, o estoque e o caixa (o PDV só acompanha).
export const gerarPixPdv = ({ venda, requestId, email, simularAprovacao = false }) =>
  chamar("mpPixPdv", { ...venda, requestId, email: email || undefined, simularAprovacao: MP_AMBIENTE === "teste" && simularAprovacao === true });
// Fechar o Pix sem pagar: cancela no Mercado Pago. Devolve { situacao } real ("pago" se já tinha sido pago).
export const cancelarPixPdv = cobrancaId => chamar("mpCancelarPdv", { cobrancaId });

// ── Loja parceira com o próprio Mercado Pago ─────────────────────
export const desconectarMercadoPagoLoja = () => chamar("mpLojaParceiraDesconectar", {});
// O servidor confere o carrinho (preço e repasse do banco) e, quando o Pix confirma, registra a venda
export const gerarPixConsignado = ({ lojaId, itens, email, requestId, simularAprovacao = false }) =>
  chamar("mpPixConsignado", { lojaId, itens, email, requestId, simularAprovacao: MP_AMBIENTE === "teste" && simularAprovacao === true });
export const verificarPixConsignado = cobrancaId => chamar("mpConsultarConsignado", { cobrancaId });
export const cancelarPixConsignado = cobrancaId => chamar("mpCancelarConsignado", { cobrancaId });

// ── Loja pública (sessão do cliente final, app Firebase "cliente") ──
export const chavePublicaLoja = (app, vendedorId) => chamar("mpChavePublicaLoja", { vendedorId }, app);
export const pagarPedidoLoja = (app, dados) => chamar("mpPagarLoja", dados, app);
export const verificarPagamentoLoja = (app, cobrancaId) => chamar("mpConsultarLoja", { cobrancaId }, app);
// Cliente fechou a janela sem pagar: o servidor cancela o Pix pendente no Mercado
// Pago e o pedido não segue. Devolve { situacao: "cancelado" | "pago" | "pendente" }.
export const cancelarPagamentoLoja = (app, vendedorId, pedidoId) => chamar("mpCancelarLoja", { vendedorId, pedidoId }, app);

export function novoRequestId() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
}

// Mensagem amigável de um erro das functions (nunca detalhes internos)
export function mensagemMP(erro) {
  const code = erro?.code || "";
  // Recusada na verificação do Firebase (App Check da loja) — não é "sessão expirada"
  if (erroDeVerificacao(erro)) return MENSAGEM_VERIFICACAO;
  if (code.includes("unauthenticated")) return "Sua sessão expirou. Entre de novo.";
  // Limite de tentativas de cartão: a mensagem do servidor já orienta (usar o Pix)
  if (code.includes("resource-exhausted")) return /cart[aã]o/i.test(erro?.message || "") ? erro.message : "Muitas tentativas. Aguarde alguns minutos.";
  if (code.includes("unavailable") || code.includes("internal") || code.includes("deadline")) return "O Mercado Pago não respondeu agora. Tente de novo em instantes.";
  return erro?.message || "Não foi possível concluir. Tente de novo.";
}
